/**
 * SuperAdminDashboard.js
 *
 * Modular organization-wide dashboard for SuperAdmin role
 * Shows: 6 governance-focused sections with organization-wide data
 * Scope: Organization-wide (all locations, all departments)
 * Financial data: FULLY VISIBLE across entire organization
 *
 * 6 Sections:
 * 1. Governance Alerts - Organization-wide alerts & actions needed
 * 2. Critical Issues & Escalations - System-wide critical items
 * 3. Financial Overview - Organization-wide financial health
 * 4. User Management & Access Control - All users, all departments
 * 5. Device Audit & Compliance Tracking - Organization-wide audit trail
 * 6. Location Performance Overview - Cross-location comparison
 *
 */

import { useState, useEffect, Fragment } from 'react';
import { useAuth } from '../../auth/hooks/useAuth';
import BaseDashboard from './BaseDashboard';
import DashboardLayout, { Section } from './DashboardLayout';
import EmptyState from '../widgets/Shared/EmptyState';
import { DeviceIDBadge, ComplaintIDBadge, StatusBadge } from '../../../shared/components/common/Badges';
import ComplaintView from '../../complaints/components/ComplaintView';
import { formatCurrencyDynamic, formatPercentage } from '../../../utils/numberUtils';
import BudgetUtilizationDisplay from '../../../shared/components/common/BudgetUtilizationDisplay';
import StatsCard from '../../../shared/components/common/StatsCard';
import { COMPLAINTS_DEFAULT_LOOKBACK_DAYS } from '../../complaints/hooks';
import { formatRoleName } from '../../../utils/roleFormatter';
import FinancialGovernanceHelpModal from '../components/FinancialGovernanceHelpModal';
import AssetCoverageByLocationModal from '../components/AssetCoverageByLocationModal';
import HighCostApprovalsByLocationModal from '../components/HighCostApprovalsByLocationModal';
import AuditComplianceSection from '../components/AuditComplianceSection';
import {
  getComplianceCardClass,
  getComplianceRateBadgeClass,
  getComplianceRateReason,
  getComplianceRateValue,
  shouldElevateComplianceCard,
} from '../utils/auditComplianceUtils';
import { ACTIVE_CRITICAL_COMPLAINTS_FILTER } from '../utils/complaintNavFilters';
import './Dashboards.css';

const buildLocationRoleGroups = (locations = []) => (
  [...locations]
    .sort((a, b) => String(a.location_name || '').localeCompare(String(b.location_name || '')))
    .map((loc) => {
      const roles = (loc.roles || [])
        .map((roleItem) => ({
          role: roleItem.role,
          active: roleItem.count || 0,
          inactive: roleItem.inactive_count || 0,
          pending: roleItem.pending_count || 0,
        }))
        .filter((roleItem) => roleItem.active + roleItem.inactive + roleItem.pending > 0)
        .sort((a, b) => b.active - a.active || String(a.role || '').localeCompare(String(b.role || '')));
      const roleRows = roles.length ? roles : [{ role: null, active: 0, inactive: 0, pending: 0 }];
      return {
        location_id: loc.location_id,
        location_name: loc.location_name,
        location_code: loc.location_code,
        active: roleRows.reduce((sum, roleItem) => sum + roleItem.active, 0),
        inactive: roleRows.reduce((sum, roleItem) => sum + roleItem.inactive, 0),
        pending: roleRows.reduce((sum, roleItem) => sum + roleItem.pending, 0),
        roles: roleRows,
      };
    })
);

const LocationPerformanceRate = ({ rate }) => {
  const value = getComplianceRateValue(rate);
  if (value === null) {
    return <span className="cs-rate-badge cs-rate-badge--na">--</span>;
  }
  return (
    <span className={`cs-rate-badge ${getComplianceRateBadgeClass(rate)}`}>
      {formatPercentage(value)}
    </span>
  );
};

const isLocationActive = (loc) => loc?.is_active !== 0 && loc?.is_active !== false;

const ComplianceRateStatCard = ({ icon, rate, label }) => {
  const value = getComplianceRateValue(rate);
  const cardClass = getComplianceCardClass(rate);
  const elevated = shouldElevateComplianceCard(rate);
  return (
    <StatsCard
      icon={icon}
      number={value === null ? 'N/A' : formatPercentage(value)}
      label={label}
      className={`${cardClass}${elevated ? ' alert-elevated' : ''}`.trim()}
      title={value === null ? getComplianceRateReason(rate) : undefined}
    />
  );
};


/**
 * SuperAdminDashboard Component - 6 Section Modular Architecture
 *
 * Organization-wide governance dashboard with enhanced UX/design patterns
 * from AdminDashboard including:
 * - MetricCard components for metrics display
 * - Grid-based card layouts with consistent styling
 * - Quick navigation sections
 * - Enhanced visual hierarchy and information density
 *
 * Props:
 * - token: JWT authentication token (optional, uses auth context if not provided)
 *
 * Note: BaseDashboard handles data fetching via useDashboardData hook.
 * This component receives data through the children function pattern.
 * Automatically uses authenticated user's role from auth context
 */
const SuperAdminDashboard = ({
  onNavigateToDevices,
  onNavigateToDevicesWithFilter,
  onNavigateToComplaintsWithFilter,
  onNavigateToUsers,
  onNavigateToLocations,
  token: providedToken,
  user: providedUser,
}) => {
  // Get authenticated user's role from context
  const { token: contextToken, user: contextUser } = useAuth();
  const token = providedToken || contextToken;
  const user = providedUser || contextUser;
  const [locationSearchQuery, setLocationSearchQuery] = useState('');
  const [selectedComplaintForModal, setSelectedComplaintForModal] = useState(null);
  const [complaintSortColumn, setComplaintSortColumn] = useState('created_at');
  const [complaintSortDirection, setComplaintSortDirection] = useState('asc');
  const [selectedAuditEvent, setSelectedAuditEvent] = useState(null);
  const [isAuditDrawerOpen, setIsAuditDrawerOpen] = useState(false);
  const [auditLocationFilter, setAuditLocationFilter] = useState(null);
  const [auditQuickFilter, setAuditQuickFilter] = useState('all'); // all|compliance|transfer|new|modified|deleted
  const [showDeviceAuditHelp, setShowDeviceAuditHelp] = useState(false);
  const [deptSearchQuery, setDeptSearchQuery] = useState('');
  const [nonCompliantSearchQuery, setNonCompliantSearchQuery] = useState('');
  const [isCompactQuickNav, setIsCompactQuickNav] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches
  );
  const [showFinancialGovernanceHelp, setShowFinancialGovernanceHelp] = useState(false);
  const [showCoverageByLocation, setShowCoverageByLocation] = useState(false);
  const [showHighCostByLocation, setShowHighCostByLocation] = useState(false);
  const [expandedUserLocationKeys, setExpandedUserLocationKeys] = useState(() => new Set());
  const [locationBudgetSortColumn, setLocationBudgetSortColumn] = useState('remaining');
  const [locationBudgetSortDirection, setLocationBudgetSortDirection] = useState('asc');

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const sync = () => setIsCompactQuickNav(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  useEffect(() => {
    if (!isAuditDrawerOpen) return;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') setIsAuditDrawerOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [isAuditDrawerOpen]);

  const scrollToSection = (sectionId) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleComplaintSort = (column) => {
    if (complaintSortColumn === column) {
      // Toggle direction if same column clicked
      setComplaintSortDirection(complaintSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New column: default to asc
      setComplaintSortColumn(column);
      setComplaintSortDirection('asc');
    }
  };

  // Render sort indicator for column headers
  const renderComplaintSortIndicator = (column) => {
    if (complaintSortColumn !== column) {
      return <span style={{ marginLeft: '0.5rem', color: '#9ca3af' }}>⇅</span>;
    }
    return complaintSortDirection === 'asc'
      ? <span style={{ marginLeft: '0.5rem', color: 'var(--dp-accent-primary)' }}>↑</span>
      : <span style={{ marginLeft: '0.5rem', color: 'var(--dp-accent-primary)' }}>↓</span>;
  };

  // Sort complaints based on selected column and direction
  const handleLocationBudgetSort = (column) => {
    if (locationBudgetSortColumn === column) {
      setLocationBudgetSortDirection((dir) => (dir === 'asc' ? 'desc' : 'asc'));
    } else {
      setLocationBudgetSortColumn(column);
      setLocationBudgetSortDirection(
        column === 'location' || column === 'code' || column === 'remaining' ? 'asc' : 'desc'
      );
    }
  };

  const renderLocationBudgetSortIndicator = (column) => {
    if (locationBudgetSortColumn !== column) {
      return <span style={{ marginLeft: '0.35rem', color: '#9ca3af' }}>⇅</span>;
    }
    return locationBudgetSortDirection === 'asc'
      ? <span style={{ marginLeft: '0.35rem', color: 'var(--dp-accent-primary)' }}>↑</span>
      : <span style={{ marginLeft: '0.35rem', color: 'var(--dp-accent-primary)' }}>↓</span>;
  };

  const getSortedLocationBudgetRows = (rows) => {
    if (!rows || rows.length === 0) return [];
    const direction = locationBudgetSortDirection === 'asc' ? 1 : -1;
    const numeric = (value) => {
      const n = Number(value);
      return Number.isFinite(n) ? n : 0;
    };
    const utilizationValue = (loc) => (
      (loc.allocated_budget ?? 0) > 0 ? numeric(loc.budget_utilization_percent) : null
    );

    return [...rows].sort((a, b) => {
      if (locationBudgetSortColumn === 'location') {
        return direction * String(a.location_name || '').localeCompare(String(b.location_name || ''));
      }
      if (locationBudgetSortColumn === 'code') {
        return direction * String(a.location_code || '').localeCompare(String(b.location_code || ''));
      }
      if (locationBudgetSortColumn === 'utilization') {
        const aVal = utilizationValue(a);
        const bVal = utilizationValue(b);
        if (aVal == null && bVal == null) return 0;
        if (aVal == null) return 1;
        if (bVal == null) return -1;
        return direction * (aVal - bVal);
      }
      const fieldMap = {
        devices: 'total_devices',
        allocated: 'allocated_budget',
        capex: 'capex_current_year',
        opex: 'contracts_current_year',
        efficiency: 'cost_efficiency_ratio',
        remaining: 'budget_remaining',
      };
      const field = fieldMap[locationBudgetSortColumn];
      return direction * (numeric(a[field]) - numeric(b[field]));
    });
  };

  const getSortedComplaints = (complaints) => {
    if (!complaints || complaints.length === 0) return [];

    return [...complaints].sort((a, b) => {
      let aValue, bValue;

      if (complaintSortColumn === 'complaint_id') {
        aValue = a.complaint_identifier || '';
        bValue = b.complaint_identifier || '';
        if (complaintSortDirection === 'asc') {
          return aValue.localeCompare(bValue);
        } else {
          return bValue.localeCompare(aValue);
        }
      } else if (complaintSortColumn === 'created_at') {
        aValue = new Date(a.created_at || 0).getTime();
        bValue = new Date(b.created_at || 0).getTime();
      } else if (complaintSortColumn === 'assigned_to') {
        aValue = a.assigned_to || '';
        bValue = b.assigned_to || '';
        if (complaintSortDirection === 'asc') {
          return aValue.localeCompare(bValue);
        } else {
          return bValue.localeCompare(aValue);
        }
      } else if (complaintSortColumn === 'status') {
        aValue = a.status || '';
        bValue = b.status || '';
        if (complaintSortDirection === 'asc') {
          return aValue.localeCompare(bValue);
        } else {
          return bValue.localeCompare(aValue);
        }
      }

      // Default numeric sort for dates
      if (complaintSortDirection === 'asc') {
        return aValue - bValue;
      } else {
        return bValue - aValue;
      }
    });
  };

  // Render function that receives data from BaseDashboard
  const renderDashboard = (dashboardPayload) => {
    // Extract data object (BaseDashboard wraps it in metadata)
    const dashboardData = dashboardPayload?.data || dashboardPayload || {};

    // Extract dashboard sections from backend API response
    const activity_metrics = dashboardData?.activity_metrics || {};
    const governance_alerts = dashboardData?.governance_alerts || {};
    const critical_issues = dashboardData?.critical_issues || {};
    const financial_overview = dashboardData?.financial_overview || {};
    const user_management = dashboardData?.user_management || {};
    const audit_events = dashboardData?.audit_events || {};
    const compliance_status = dashboardData?.compliance_status || {};
    const hasAuditComplianceData = (
      Object.keys(audit_events).length > 0 || Object.keys(compliance_status).length > 0
    );
    const location_performance = dashboardData?.location_performance || {};

    const complianceRateValue = (metric) => {
      if (metric && typeof metric === 'object') {
        return metric.value === undefined ? null : metric.value;
      }
      if (metric === null || metric === undefined) return null;
      return metric;
    };

    const getStatusColor = (rate) => {
      if (rate === null || rate === undefined) return '#9ca3af';
      if (rate >= 80) return '#28a745';
      if (rate >= 60) return '#ffc107';
      return '#dc3545';
    };

    const ppmRate = complianceRateValue(governance_alerts?.compliance_metrics?.ppm_compliance_rate);
    const calibrationRate = complianceRateValue(governance_alerts?.compliance_metrics?.calibration_compliance_rate);
    const regulatoryRate = complianceRateValue(governance_alerts?.compliance_metrics?.regulatory_compliance_rate);
    const fgAnnual = Number(financial_overview.annual_budget ?? 0);
    const fgHasBudget = fgAnnual > 0;
    const fgYtdCapex = Number(financial_overview.ytd_capex ?? 0);
    const fgYtdOpex = Number(financial_overview.ytd_opex ?? 0);
    const fgCySpend =
      financial_overview.ytd_spend != null && financial_overview.ytd_spend !== ''
        ? Number(financial_overview.ytd_spend)
        : fgYtdCapex + fgYtdOpex;
    const financeAlert = fgHasBudget && !!financial_overview.budget_overrun;
    const financeUnbudgeted = !fgHasBudget;
    const coverageLocations = governance_alerts?.location_coverage?.locations || [];
    const coverageHasGaps = (governance_alerts?.location_coverage?.status || (coverageLocations.length ? 'gaps' : 'complete')) === 'gaps';
    const atRiskCount = governance_alerts?.at_risk_locations?.count
      ?? (governance_alerts.location_issues || []).length;
    const auditAlertCount = governance_alerts?.audit_alerts?.count
      ?? audit_events.suspicious_events
      ?? 0;

    return (
      <DashboardLayout columns="1" gap="md">

        {/* ========================================
            QUICK NAVIGATION
            ======================================== */}
        <div
          className={`admin-quick-nav${isCompactQuickNav ? ' admin-quick-nav--compact' : ''}`}
        >
          <div style={{ fontSize: '0.9rem', fontWeight: '600', marginBottom: '0.75rem', color: '#6b7280' }}>
            📍 Quick Navigation
          </div>
          <div className="admin-quick-nav-scroll">
            <div
              className="admin-quick-nav-links"
              style={{
                display: 'flex',
                flexWrap: isCompactQuickNav ? 'nowrap' : 'wrap',
                gap: '0.5rem'
              }}
            >
              {[
                { label: '📊 Overview', id: 'statistics-overview' },
                { label: '🚨 Alerts', id: 'governance-alerts' },
                { label: '⚠️ Critical Complaints', id: 'critical-complaints' },
                { label: '💰 Financial', id: 'financial-overview' },
                { label: '👥 Users', id: 'user-management' },
                { label: '📋 Audit', id: 'audit-events' },
                { label: '📊 Locations', id: 'location-performance' }
              ].map((link) => (
                <a
                  key={link.id}
                  href={`#${link.id}`}
                  className="admin-quick-nav-link"
                  style={{
                    padding: '0.5rem 0.75rem',
                    backgroundColor: '#f3f4f6',
                    border: '1px solid #e5e7eb',
                    borderRadius: '4px',
                    fontSize: '0.85rem',
                    color: 'var(--dp-accent-primary)',
                    textDecoration: 'none',
                    fontWeight: '500',
                    transition: 'all 0.2s',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#e5e7eb';
                    e.target.style.borderColor = '#9ca3af';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = '#f3f4f6';
                    e.target.style.borderColor = '#e5e7eb';
                  }}
                >
                  {link.label}
                </a>
              ))}
            </div>
          </div>
        </div>

        <Section id="statistics-overview" title="📊 Statistics Overview">
          <div className="list-default-window-note" role="note">
            <span className="list-default-window-note__icon" aria-hidden="true">ℹ️</span>
            <span>
              Organization-wide across all locations. Complaint totals use the same default as the
              Complaints list: open tickets (any age) plus closed/cancelled from the{' '}
              <strong>last {COMPLAINTS_DEFAULT_LOOKBACK_DAYS} days</strong>.
            </span>
          </div>
          <div className="dashboard-stats-summary">
            {[
              {
                label: 'Locations',
                value: activity_metrics.total_locations || 0,
                icon: '📍',
                className: 'total',
                action: 'locations',
                title: 'All locations. Click to open Locations.',
              },
              {
                label: 'Total Devices',
                value: activity_metrics.total_devices || 0,
                icon: '📱',
                className: 'total',
                action: 'view-all-devices',
                title: 'Devices across all locations. Click to open Devices.',
              },
              {
                label: 'Devices with Issues',
                value: activity_metrics.devices_with_issues || 0,
                icon: '⚠️',
                className: 'open',
                action: 'devices-with-issues',
                title: 'Devices with at least one active complaint. Click to open Devices.',
              },
              {
                label: 'Total Complaints',
                value: activity_metrics.total_complaints || 0,
                icon: '📝',
                className: 'pending',
                action: 'all-complaints',
                title: 'Open tickets (any age) + closed/cancelled in last 30 days. Click to open Complaints.',
              },
              {
                label: 'Critical Complaints',
                value: activity_metrics.critical_complaints || 0,
                icon: '🔴',
                className: 'uncovered',
                action: 'critical-complaints',
                elevateWhenPositive: true,
                title: 'Active critical complaints (open, acknowledged, in progress, escalated to vendor). Click to open Complaints.',
              },
              {
                label: 'Overdue Maintenance',
                value: activity_metrics.overdue_maintenance || 0,
                icon: '🔧',
                className: 'maintenance',
                action: 'overdue-maintenance',
                elevateWhenPositive: true,
                title: 'Devices with overdue PPM, calibration, or compliance. Click to open Devices.',
              },
              {
                label: 'At-Risk Locations',
                value: activity_metrics.locations_needing_attention || 0,
                icon: '⚠️',
                className: 'open',
                action: 'at-risk-locations',
                elevateWhenPositive: true,
                title: 'Locations with critical complaints or overdue maintenance. Click to see the breakdown in Governance Alerts.',
              },
              {
                label: 'Users to Review',
                value: activity_metrics.users_needing_action || 0,
                icon: '👥',
                className: 'deactivated',
                action: 'users-to-review',
                elevateWhenPositive: true,
                title: 'Pending verification or deactivated users. Click to open Users.',
              },
            ].map((stat) => {
              const handleStatClick = () => {
                switch (stat.action) {
                  case 'locations':
                    onNavigateToLocations && onNavigateToLocations();
                    break;
                  case 'view-all-devices':
                    onNavigateToDevicesWithFilter && onNavigateToDevicesWithFilter({});
                    break;
                  case 'devices-with-issues':
                    onNavigateToDevicesWithFilter && onNavigateToDevicesWithFilter({
                      hasActiveComplaints: true,
                    });
                    break;
                  case 'all-complaints':
                    onNavigateToComplaintsWithFilter && onNavigateToComplaintsWithFilter({});
                    break;
                  case 'critical-complaints':
                    onNavigateToComplaintsWithFilter && onNavigateToComplaintsWithFilter(
                      ACTIVE_CRITICAL_COMPLAINTS_FILTER
                    );
                    break;
                  case 'overdue-maintenance':
                    onNavigateToDevicesWithFilter && onNavigateToDevicesWithFilter({
                      ppmStatus: ['Overdue (0-30d)', 'Overdue (30+d)'],
                      calibrationStatus: ['Overdue (0-30d)', 'Overdue (30+d)'],
                      complianceStatus: ['Overdue (0-30d)', 'Overdue (30+d)'],
                    });
                    break;
                  case 'at-risk-locations':
                    scrollToSection('at-risk-locations');
                    break;
                  case 'users-to-review':
                    onNavigateToUsers && onNavigateToUsers('deactivated');
                    break;
                  default:
                    break;
                }
              };

              const elevate = stat.elevateWhenPositive && Number(stat.value) > 0;

              return (
                <StatsCard
                  key={stat.action}
                  icon={stat.icon}
                  number={stat.value}
                  label={stat.label}
                  className={`${stat.className}${elevate ? ' alert-elevated' : ''}`.trim()}
                  onClick={handleStatClick}
                  title={stat.title || `Click to view ${stat.label.toLowerCase()}`}
                />
              );
            })}
          </div>
        </Section>

        <Section id="governance-alerts" title="🚨 Governance Alerts & Actions Needed">
          <div className="admin-governance-alerts-grid">
            <div className="admin-governance-alerts-row admin-governance-alerts-row--4 admin-governance-alerts-row--top">
              <div
                className="admin-governance-card admin-governance-card--span-2"
                onClick={() => {
                  if (!onNavigateToUsers) {
                    scrollToSection('user-management');
                    return;
                  }
                  const gapLocationIds = coverageLocations
                    .map((loc) => loc.location_id)
                    .filter((id) => id != null);
                  onNavigateToUsers(null, gapLocationIds.length ? gapLocationIds : undefined);
                }}
                title="View users at locations with coverage gaps"
              >
                <div className="admin-at-risk-head">
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                      <span style={{ fontSize: '1.1rem' }}>👥</span>
                      <div style={{ fontSize: '0.9rem', color: '#666' }}>Location Coverage</div>
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#999' }}>
                      {coverageHasGaps
                        ? 'Locations missing at least one required persona'
                        : 'Every location has admin, manager, technician, coordinator, custodian, and help desk'}
                    </div>
                  </div>
                  <div style={{ fontSize: '1.25rem', fontWeight: 'bold', lineHeight: 1.2, color: coverageHasGaps ? '#f57c00' : '#4caf50' }}>
                    {coverageHasGaps ? 'Gaps' : 'Complete'}
                  </div>
                </div>
                {coverageLocations.length > 0 && (
                  <div className="admin-at-risk-list admin-at-risk-list--coverage">
                    {coverageLocations.map((loc) => {
                      const missing = loc.missing || [];
                      const openLocationUsers = (event) => {
                        event.stopPropagation();
                        if (onNavigateToUsers) {
                          onNavigateToUsers(null, loc.location_id ? [loc.location_id] : undefined);
                        } else {
                          scrollToSection('user-management');
                        }
                      };
                      return (
                        <div
                          key={loc.location_id || loc.location}
                          className="admin-at-risk-row admin-at-risk-row--coverage"
                          role="button"
                          tabIndex={0}
                          onClick={openLocationUsers}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              openLocationUsers(event);
                            }
                          }}
                        >
                          <span className="admin-at-risk-row__name">{loc.location}</span>
                          {missing.length > 0 && (
                            <div className="admin-coverage-gaps" aria-label={`Missing: ${missing.map((gap) => formatRoleName(gap)).join(', ')}`}>
                              {missing.map((gap) => (
                                <span key={gap} className="admin-coverage-gap-chip">
                                  {formatRoleName(gap)}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
                <div style={{ fontSize: '0.75rem', color: 'var(--dp-accent-primary)', marginTop: '0.75rem', fontWeight: '500' }}>
                  View users →
                </div>
              </div>

              <div
                id="at-risk-locations"
                className="admin-governance-card admin-governance-card--span-2"
                onClick={() => scrollToSection('location-performance')}
                title="View Location Performance for more detail"
              >
                <div className="admin-at-risk-head">
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                      <span style={{ fontSize: '1.1rem' }}>📍</span>
                      <div style={{ fontSize: '0.9rem', color: '#666' }}>At-Risk Locations</div>
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#999' }}>
                      Critical complaints or overdue maintenance
                    </div>
                  </div>
                  <div style={{ fontSize: '1.25rem', fontWeight: 'bold', lineHeight: 1, color: atRiskCount > 0 ? '#d32f2f' : '#4caf50' }}>
                    {atRiskCount}
                  </div>
                </div>
                {governance_alerts.location_issues && governance_alerts.location_issues.length > 0 && (
                  <div className="admin-at-risk-list">
                    {governance_alerts.location_issues.map((loc) => {
                      const reasons = [
                        loc.critical_count > 0
                          ? `${loc.critical_count} critical`
                          : null,
                        loc.overdue_count > 0
                          ? `${loc.overdue_count} overdue`
                          : null,
                      ].filter(Boolean).join(' · ');
                      return (
                        <div key={loc.location_id || loc.location} className="admin-at-risk-row">
                          <span className="admin-at-risk-row__name">{loc.location}</span>
                          <span className="admin-at-risk-row__reasons">{reasons}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
                <div style={{ fontSize: '0.75rem', color: 'var(--dp-accent-primary)', marginTop: '0.75rem', fontWeight: '500' }}>
                  View location performance →
                </div>
              </div>
            </div>

            <div className="admin-governance-alerts-row admin-governance-alerts-row--4 admin-governance-alerts-row--top">
              <div
                className="admin-governance-card admin-governance-card--no-hover"
                title="Location admins action device approvals"
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <span style={{ fontSize: '1.1rem' }}>📋</span>
                  <div style={{ fontSize: '0.9rem', color: '#666' }}>Device Approvals Pending</div>
                </div>
                <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: governance_alerts?.pending_approvals?.count > 0 ? '#d32f2f' : '#4caf50' }}>
                  {governance_alerts?.pending_approvals?.count || 0}
                </div>
                {governance_alerts?.pending_approvals?.count > 0 && governance_alerts?.pending_approvals?.oldest_days != null && (
                  <div style={{ fontSize: '0.8rem', color: '#999', marginTop: '0.5rem' }}>
                    Oldest: {governance_alerts.pending_approvals.oldest_days} days
                  </div>
                )}
                {(!governance_alerts?.pending_approvals?.count || governance_alerts.pending_approvals.count === 0) && (
                  <div style={{ fontSize: '0.8rem', color: '#4caf50', marginTop: '0.5rem' }}>
                    All devices approved
                  </div>
                )}
                <div style={{ fontSize: '0.75rem', color: '#999', marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid #e5e7eb' }}>
                  Location admins action these
                </div>
              </div>

              <div
                className="admin-governance-card"
                onClick={() => scrollToSection('audit-events')}
                title="View Device Audit"
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <span style={{ fontSize: '1.1rem' }}>📊</span>
                  <div style={{ fontSize: '0.9rem', color: '#666' }}>Compliance Metrics</div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                  {[
                    { key: 'ppm', label: 'PPM', rate: ppmRate },
                    { key: 'calibration', label: 'Calibration', rate: calibrationRate },
                    { key: 'regulatory', label: 'Regulatory', rate: regulatoryRate },
                  ].map((item) => (
                    <div key={item.key}>
                      <div style={{ fontSize: '0.75rem', color: '#999', marginBottom: '0.2rem' }}>{item.label}</div>
                      {item.rate === null ? (
                        <div style={{ fontSize: '0.9rem', fontWeight: '600', color: '#9ca3af' }}>N/A</div>
                      ) : (
                        <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: getStatusColor(item.rate) }}>
                          {formatPercentage(item.rate)}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#999', marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid #e5e7eb' }}>
                  Color: Green ≥80%, Amber 60-79%, Red &lt;60%
                </div>
              </div>

              <div
                className="admin-governance-card"
                onClick={() => scrollToSection('financial-overview')}
                title="View Financial Overview"
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <span style={{ fontSize: '1.1rem' }}>💵</span>
                  <div style={{ fontSize: '0.9rem', color: '#666' }}>Financial Governance</div>
                </div>
                <BudgetUtilizationDisplay
                  large
                  allocatedBudget={financial_overview.annual_budget}
                  budgetUtilizationPercent={fgHasBudget ? financial_overview.budget_utilization_percent : undefined}
                  budgetOverrun={!!financial_overview.budget_overrun}
                  scope="organization"
                />
                {financeAlert ? (
                  <div style={{ fontSize: '0.8rem', color: '#c62828', marginTop: '0.5rem' }}>
                    Organization budget overrun
                  </div>
                ) : fgHasBudget ? (
                  <div style={{ fontSize: '0.8rem', color: '#4caf50', marginTop: '0.5rem' }}>
                    Organization budget on track
                  </div>
                ) : (
                  <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: '0.5rem' }}>
                    Annual budget not set
                  </div>
                )}
                {(fgAnnual > 0 || fgCySpend > 0 || (financial_overview.pending_cost ?? 0) > 0) && (
                  <div style={{ fontSize: '0.75rem', color: '#999', marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid #e5e7eb' }}>
                    <div style={{ marginBottom: '0.45rem', fontWeight: '600', color: '#64748b' }}>
                      Current calendar year (Jan–Dec), approved devices
                    </div>
                    {fgHasBudget ? (
                      <div style={{ marginBottom: '0.4rem' }}>
                        Annual budget: {formatCurrencyDynamic(financial_overview.annual_budget)}
                      </div>
                    ) : (
                      <div style={{ marginBottom: '0.4rem', color: '#94a3b8' }}>
                        Annual budget not set
                      </div>
                    )}
                    <div style={{ marginBottom: '0.25rem' }}>
                      Calendar-year spend (approved): {formatCurrencyDynamic(fgCySpend)}
                    </div>
                    <div style={{ marginBottom: '0.4rem', fontSize: '0.68rem', color: '#94a3b8' }}>
                      YTD Capex {formatCurrencyDynamic(fgYtdCapex)}
                      {' · '}
                      YTD Opex {formatCurrencyDynamic(fgYtdOpex)}
                    </div>
                    {(financial_overview.pending_cost ?? 0) > 0 && (
                      <div style={{ paddingTop: '0.4rem', borderTop: '1px solid #e5e7eb', marginTop: '0.4rem', color: '#f57c00', fontWeight: '500' }}>
                        Pending approvals: {formatCurrencyDynamic(financial_overview.pending_cost)}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div
                className="admin-governance-card"
                onClick={() => scrollToSection('audit-events')}
                title="View Device Audit & Compliance Tracking"
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <span style={{ fontSize: '1.1rem' }}>🔍</span>
                  <div style={{ fontSize: '0.9rem', color: '#666' }}>Device Audit Activity</div>
                </div>
                <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: auditAlertCount > 0 ? '#d32f2f' : '#4caf50' }}>
                  {auditAlertCount}
                </div>
                {auditAlertCount > 0 ? (
                  <div style={{ fontSize: '0.8rem', color: '#d32f2f', marginTop: '0.5rem' }}>
                    {governance_alerts?.audit_alerts?.summary || 'Suspicious activity detected'}
                  </div>
                ) : (
                  <div style={{ fontSize: '0.8rem', color: '#4caf50', marginTop: '0.5rem' }}>
                    Audit activity normal
                  </div>
                )}
                <div style={{ fontSize: '0.75rem', color: '#999', marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid #e5e7eb', fontStyle: 'italic' }}>
                  Flags devices with &gt;5 changes in 30 days
                </div>
              </div>
            </div>
          </div>

        </Section>

        {/* ========================================
            SECTION 2: TOP CRITICAL COMPLAINTS (ORG WIDE)
            ======================================== */}
        <Section
          id="critical-complaints"
          title={(
            <span
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
                gap: '1rem',
              }}
            >
              <span>
                {`⚠️ Top Critical Complaints${critical_issues.critical_issues && critical_issues.critical_issues.length > 0 ? ` (${critical_issues.critical_issues.length})` : ''}`}
              </span>
              {onNavigateToComplaintsWithFilter ? (
                <button
                  type="button"
                  className="btn-view-all-devices"
                  onClick={() => onNavigateToComplaintsWithFilter(ACTIVE_CRITICAL_COMPLAINTS_FILTER)}
                  title="View all active critical complaints in Complaints"
                  style={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                >
                  View all critical in Complaints ›
                </button>
              ) : null}
            </span>
          )}
        >
          {critical_issues.critical_issues && critical_issues.critical_issues.length > 0 ? (
            <>
              {/* Help Text */}
              <div style={{
                backgroundColor: 'var(--dp-accent-focus-ring)',
                padding: '0.75rem',
                borderRadius: '6px',
                border: '1px solid var(--dp-accent-focus-ring)',
                marginBottom: '1rem',
                fontSize: '0.75rem',
                color: 'var(--dp-accent-secondary)',
                lineHeight: '1.5'
              }}>
                <div style={{ fontWeight: '600', marginBottom: '0.3rem' }}>ℹ️ Selection Criteria:</div>
                <div>Showing top 10 critical complaints prioritized by urgency:</div>
                <div style={{ marginTop: '0.4rem', marginLeft: '0.5rem' }}>
                  <div>1. <strong>Unassigned (Open)</strong> - highest priority</div>
                  <div>2. <strong>In Progress</strong> - being worked</div>
                  <div>3. <strong>Oldest First</strong> - SLA risk indicators</div>
                </div>
              </div>
              <div className="critical-complaints-table-container">
              <table className="critical-complaints-table">
                <thead>
                  <tr>
                    <th className="col-rank">#</th>
                    <th
                      className="col-complaint"
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleComplaintSort('complaint_id')}
                    >
                      Complaint {renderComplaintSortIndicator('complaint_id')}
                    </th>
                    <th
                      className="col-device"
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleComplaintSort('device_id')}
                    >
                      Device {renderComplaintSortIndicator('device_id')}
                    </th>
                    <th className="col-department">Department</th>
                    <th className="col-location">Location</th>
                    <th
                      className="col-reported"
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleComplaintSort('created_at')}
                    >
                      Reported {renderComplaintSortIndicator('created_at')}
                    </th>
                    <th
                      className="col-assigned"
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleComplaintSort('assigned_to')}
                    >
                      Assigned To {renderComplaintSortIndicator('assigned_to')}
                    </th>
                    <th
                      className="col-status"
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleComplaintSort('status')}
                    >
                      Status {renderComplaintSortIndicator('status')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {getSortedComplaints(critical_issues.critical_issues.slice(0, 10)).map((complaint, index) => {
                    const getDaysSince = (dateString) => {
                      if (!dateString) return 'N/A';
                      const date = new Date(dateString);
                      const today = new Date();
                      const days = Math.floor((today - date) / (1000 * 60 * 60 * 24));
                      if (days === 0) return 'Today';
                      if (days === 1) return '1 day ago';
                      return `${days} days ago`;
                    };

                    return (
                      <tr
                        key={index}
                        className="complaint-row clickable"
                        style={{ cursor: 'pointer' }}
                        onClick={() => setSelectedComplaintForModal(complaint)}
                      >
                        <td className="col-rank">
                          <span className="rank-badge">{index + 1}</span>
                        </td>
                        <td className="col-complaint complaint-stacked-cell">
                          <div className="stacked-content">
                            <span className="complaint-title">{complaint.title}</span>
                            <ComplaintIDBadge complaintId={complaint.complaint_identifier || 'N/A'} size="small" />
                          </div>
                        </td>
                        <td className="col-device device-stacked-cell">
                          <div className="stacked-content">
                            <span className="device-name">{complaint.device_name}</span>
                            <DeviceIDBadge deviceId={complaint.device_identifier || 'N/A'} size="small" />
                          </div>
                        </td>
                        <td className="col-department">
                          <span className="department-icon-small">{complaint.department_icon || '🏢'}</span>
                          <span className="department-text">{complaint.department || 'N/A'}</span>
                        </td>
                        <td className="col-location">
                          <span className="device-location" 
                            style={{ fontWeight: '500', fontSize: '0.8rem', color: '#6c757d', marginTop: '4px' }}>
                              📍 {complaint.location || 'N/A'}
                          </span>
                        </td>
                        <td className="col-reported">
                          <span className="reported-text">{getDaysSince(complaint.created_at)}</span>
                        </td>
                        <td className="col-assigned">
                          <span className="assigned-text">{complaint.assigned_to || '—'}</span>
                        </td>
                        <td className="col-status center">
                          <StatusBadge status={complaint.status || 'unknown'} size="small" />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            </>
          ) : (
            <EmptyState section="Critical Issues" message="No critical complaints at this time." icon="✅" />
          )}
        </Section>

        {/* ========================================
            SECTION 3: FINANCIAL OVERVIEW
            ======================================== */}
        <Section
          id="financial-overview"
          title={(
            <span
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
                gap: '1rem',
              }}
            >
              <span>💰 Financial Overview</span>
              <button
                type="button"
                className="user-mgmt-help-btn"
                onClick={() => setShowFinancialGovernanceHelp(true)}
                aria-label="Financial overview help"
                title="What do these metrics mean?"
                style={{ flexShrink: 0 }}
              >
                ?
              </button>
            </span>
          )}
        >
          {financial_overview && Object.keys(financial_overview).length > 0 ? (
            <>
              <div className="list-default-window-note" role="note">
                <span className="list-default-window-note__icon" aria-hidden="true">ℹ️</span>
                <span>
                  Organization-wide calendar-year YTD. <strong>Capex</strong> = approved devices;{' '}
                  <strong>Opex</strong> = pro-rated contracts. Pending = budget impact if location admins approve.
                  Annual budget is the sum of location budgets.
                </span>
              </div>

              <div className={`fg-budget-card ${financeAlert ? 'fg-budget-card--alert' : financeUnbudgeted ? 'fg-budget-card--unbudgeted' : 'fg-budget-card--ok'}`}>
                <div className={`fg-budget-card__header ${financeAlert ? 'fg-budget-card__header--alert' : financeUnbudgeted ? 'fg-budget-card__header--unbudgeted' : 'fg-budget-card__header--ok'}`}>
                  {financeAlert ? '⚠️ Budget Alert' : financeUnbudgeted ? '📌 Calendar-year spend (annual budget not set)' : '✅ Budget On Track'}
                </div>
                {(() => {
                  const annualB = financial_overview.annual_budget ?? 0;
                  const hasBudget = annualB > 0;
                  const pctOfBudget = (amount) => (
                    hasBudget ? formatPercentage((Number(amount) / annualB) * 100) : null
                  );
                  const utilizationPct = fgHasBudget
                    ? financial_overview.budget_utilization_percent
                    : null;

                  return (
                    <div className="fg-budget-card__metrics">
                      <div className="fg-budget-card__metric-cell">
                        <div className="fg-budget-card__metric-label">Annual Budget</div>
                        <div className="fg-budget-card__metric-value">
                          {formatCurrencyDynamic(financial_overview.annual_budget)}
                        </div>
                        <div className="fg-budget-card__metric-subtext">Sum of location budgets</div>
                      </div>
                      <div className="fg-budget-card__metric-separator" aria-hidden="true" />
                      <div className="fg-budget-card__metric-cell">
                        <div className="fg-budget-card__metric-label">YTD Capex</div>
                        <div className={`fg-budget-card__metric-value fg-budget-card__metric-value--capex${financeAlert ? ' fg-budget-card__metric-value--capex-alert' : ''}`}>
                          {formatCurrencyDynamic(financial_overview.ytd_capex)}
                          {financeAlert && (
                            <span style={{ fontSize: '0.8rem', color: '#d32f2f' }}>⚠️</span>
                          )}
                        </div>
                        <div className="fg-budget-card__metric-subtext">
                          {hasBudget
                            ? <>{pctOfBudget(financial_overview.ytd_capex)} of budget</>
                            : <span className="fg-budget-card__metric-subtext--muted">Calendar-year approved devices</span>}
                        </div>
                      </div>
                      <div className="fg-budget-card__metric-separator" aria-hidden="true" />
                      <div className="fg-budget-card__metric-cell">
                        <div className="fg-budget-card__metric-label">YTD Opex</div>
                        <div className="fg-budget-card__metric-value fg-budget-card__metric-value--opex">
                          {formatCurrencyDynamic(financial_overview.ytd_opex)}
                        </div>
                        <div className="fg-budget-card__metric-subtext">
                          {hasBudget
                            ? <>{pctOfBudget(financial_overview.ytd_opex)} of budget</>
                            : <span className="fg-budget-card__metric-subtext--muted">Pro-rated contracts</span>}
                        </div>
                      </div>
                      <div className="fg-budget-card__metric-separator" aria-hidden="true" />
                      <div className="fg-budget-card__metric-cell">
                        <div className="fg-budget-card__metric-label">Pending Approvals</div>
                        <div className="fg-budget-card__metric-value fg-budget-card__metric-value--pending">
                          {formatCurrencyDynamic(financial_overview.pending_cost)}
                        </div>
                        <div className="fg-budget-card__metric-subtext">
                          {hasBudget
                            ? <>{pctOfBudget(financial_overview.pending_cost)} of budget</>
                            : <span className="fg-budget-card__metric-subtext--muted">If location admins approve</span>}
                        </div>
                      </div>
                      <div className="fg-budget-card__metric-separator" aria-hidden="true" />
                      <div className="fg-budget-card__metric-cell">
                        <div className="fg-budget-card__metric-label">Remaining</div>
                        <div className={`fg-budget-card__metric-value ${financial_overview.budget_remaining > 0 ? 'fg-budget-card__metric-value--remaining-ok' : 'fg-budget-card__metric-value--remaining-bad'}`}>
                          {formatCurrencyDynamic(financial_overview.budget_remaining)}
                        </div>
                        <div className="fg-budget-card__metric-subtext">
                          {hasBudget
                            ? <>{pctOfBudget(financial_overview.budget_remaining)} of budget</>
                            : <span className="fg-budget-card__metric-subtext--muted">After YTD spend</span>}
                        </div>
                      </div>
                      <div className="fg-budget-card__metric-separator" aria-hidden="true" />
                      <div className="fg-budget-card__metric-cell">
                        <div className="fg-budget-card__metric-label">Utilization</div>
                        {fgHasBudget && utilizationPct != null ? (
                          <>
                            <div className={`fg-budget-card__metric-value fg-budget-card__metric-value--utilization${financial_overview.budget_overrun ? ' fg-budget-card__metric-value--utilization-over' : ''}`}>
                              {formatPercentage(utilizationPct)}
                            </div>
                            <div className="fg-budget-card__metric-subtext">YTD total utilization</div>
                          </>
                        ) : (
                          <>
                            <div className="fg-budget-card__metric-value" style={{ fontSize: '1rem' }}>
                              Unbudgeted
                            </div>
                            <div className="fg-budget-card__metric-subtext fg-budget-card__metric-subtext--muted">
                              Set location budgets to track
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })()}

                {financial_overview.pending_cost > 0 && (
                  (() => {
                    const currentSpend = financial_overview.ytd_capex + financial_overview.ytd_opex;
                    const projectedSpend = currentSpend + financial_overview.pending_cost;
                    const annualB = financial_overview.annual_budget ?? 0;
                    const hasBudget = annualB > 0;
                    const isAlreadyOverBudget = hasBudget && currentSpend > annualB;
                    const willExceedBudget = hasBudget && projectedSpend > annualB;

                    return (
                      <div className={`fg-budget-card__impact${isAlreadyOverBudget ? ' fg-budget-card__impact--over' : ''}`}>
                        <div className="fg-budget-card__impact-head">
                          📋 <strong>Budget Impact</strong> - If all pending approvals are approved:
                        </div>
                        <div className="fg-budget-card__impact-row">
                          <span>
                            Total spend: {formatCurrencyDynamic(projectedSpend)}
                            {hasBudget
                              ? <> ({formatPercentage((projectedSpend / annualB) * 100)} utilization)</>
                              : <> (no annual budget allocated — utilization not calculated)</>}
                          </span>
                          <span style={{ fontWeight: '600', color: isAlreadyOverBudget ? '#d32f2f' : willExceedBudget ? '#d32f2f' : '#2e7d32' }}>
                            {isAlreadyOverBudget && willExceedBudget && 'Already exceeded + further exceed'}
                            {isAlreadyOverBudget && !willExceedBudget && 'Already over budget'}
                            {!isAlreadyOverBudget && willExceedBudget && 'Would exceed'}
                            {!isAlreadyOverBudget && !willExceedBudget && 'Within budget'}
                          </span>
                        </div>
                        {isAlreadyOverBudget && (
                          <div className="fg-budget-card__impact-detail">
                            Current approved spend ({formatCurrencyDynamic(currentSpend)}) has already exceeded the annual budget ({formatCurrencyDynamic(annualB)}) by {formatCurrencyDynamic(currentSpend - annualB)}.
                            {willExceedBudget && (
                              <div style={{ marginTop: '0.25rem' }}>
                                Approving pending items will further increase overrun by {formatCurrencyDynamic(financial_overview.pending_cost)}.
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })()
                )}
              </div>

              {/* ========================================
                  FINANCIAL METRICS CARDS GRID
                  ======================================== */}
              <div className="financial-cards-grid responsive-grid responsive-grid-200" style={{ marginBottom: '1.5rem' }}>

                {/* Total Capex Card */}
                <div style={{
                  backgroundColor: '#fff',
                  padding: '1rem',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  borderLeft: '4px solid #1976d2'
                }}>
                  <div style={{ fontSize: '0.75rem', color: '#999', marginBottom: '0.5rem', fontWeight: '600' }}>
                    💰 Total Capex
                  </div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1976d2', marginBottom: '0.5rem' }}>
                    {formatCurrencyDynamic(financial_overview.total_capex)}
                  </div>
                  <div style={{ fontSize: '0.7rem', color: '#4b5563', fontWeight: '500', lineHeight: '1.4' }}>
                    Total device investment (all locations, approved devices)
                  </div>
                </div>

                {/* Total Maintenance Contracts Card */}
                <div style={{
                  backgroundColor: '#fff',
                  padding: '1rem',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  borderLeft: '4px solid #4caf50'
                }}>
                  <div style={{ fontSize: '0.75rem', color: '#999', marginBottom: '0.5rem', fontWeight: '600' }}>
                    📋 Total Maintenance Contracts
                  </div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#4caf50', marginBottom: '0.5rem' }}>
                    {formatCurrencyDynamic(financial_overview.total_maintenance_contracts_cost)}
                  </div>
                  <div style={{ fontSize: '0.7rem', color: '#4b5563', fontWeight: '500', lineHeight: '1.4' }}>
                    LAMC + CAMC cost (all locations)
                  </div>
                </div>

                {/* Annual Maintenance Contract Cost Card */}
                <div style={{
                  backgroundColor: '#fff',
                  padding: '1rem',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  borderLeft: '4px solid #f57c00'
                }}>
                  <div style={{ fontSize: '0.75rem', color: '#999', marginBottom: '0.5rem', fontWeight: '600' }}>
                    💸 Annual Contract Cost
                  </div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#f57c00', marginBottom: '0.5rem' }}>
                    {formatCurrencyDynamic(financial_overview.annual_contract_cost)}
                  </div>
                  <div style={{ fontSize: '0.7rem', color: '#4b5563', fontWeight: '500', lineHeight: '1.4' }}>
                    Yearly maintenance contract cost (all locations)
                  </div>
                </div>

                {/* Contract-to-Value Ratio Card */}
                <div style={{
                  backgroundColor: financial_overview.contract_to_value_ratio > 15 ? '#f3e5f5' : '#fff',
                  padding: '1rem',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  borderLeft: '4px solid #9c27b0'
                }}>
                  <div style={{ fontSize: '0.75rem', color: '#999', marginBottom: '0.5rem', fontWeight: '600' }}>
                    📈 Contract-to-Value Ratio
                  </div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: financial_overview.contract_to_value_ratio > 15 ? '#c2185b' : '#9c27b0', marginBottom: '0.5rem' }}>
                    {formatPercentage(financial_overview.contract_to_value_ratio || 0)}
                  </div>
                  <div style={{ fontSize: '0.7rem', color: '#4b5563', fontWeight: '500', lineHeight: '1.4' }}>
                    Annual maintenance as % of total device value
                  </div>
                  {financial_overview.contract_to_value_ratio > 15 && (
                    <div style={{ fontSize: '0.65rem', color: '#c2185b', marginTop: '0.5rem', fontWeight: '500' }}>
                      ⚠️ High maintenance ratio
                    </div>
                  )}
                </div>
              </div>

              <div className="dashboard-subsection-label">Approvals & coverage</div>

              <div className="fg-approvals-coverage-grid">
                <div className="fg-high-cost-strip">
                  <div className="fg-high-cost-strip__header">
                    <div className="fg-high-cost-strip__title">⚠️ High-Cost Approvals</div>
                    <button
                      type="button"
                      className="fg-high-cost-strip__link"
                      onClick={() => setShowHighCostByLocation(true)}
                    >
                      By location →
                    </button>
                  </div>
                  <div className="fg-high-cost-strip__metrics">
                    <div>
                      <div className="fg-high-cost-strip__metric-label">Pending above location threshold</div>
                      <div className="fg-high-cost-strip__metric-value fg-high-cost-strip__metric-value--pending">
                        {financial_overview.high_cost_pending || 0}
                      </div>
                    </div>
                  </div>
                  {(() => {
                    const highCostRows = financial_overview.location_high_cost_summary || [];
                    const locationsWithPending = highCostRows.filter((row) => (row.pending_count || 0) > 0).length;
                    if ((financial_overview.high_cost_pending || 0) > 0) {
                      return (
                        <p className="fg-high-cost-strip__note fg-high-cost-strip__note--block">
                          Across {locationsWithPending} location{locationsWithPending !== 1 ? 's' : ''}. Location admins action these.
                        </p>
                      );
                    }
                    return (
                      <p className="fg-high-cost-strip__empty">No high-cost devices pending approval.</p>
                    );
                  })()}
                </div>

                <div className="fg-coverage-card">
                  <div className="fg-coverage-card__header">
                    <div className="fg-coverage-card__title">🔧 Asset Coverage Snapshot</div>
                    <button
                      type="button"
                      className="fg-coverage-card__link"
                      onClick={() => setShowCoverageByLocation(true)}
                    >
                      By location →
                    </button>
                  </div>
                  <div className="fg-coverage-card__metrics">
                    <div>
                      <div className="fg-coverage-card__metric-label">Coverage Expiring</div>
                      <div className="fg-coverage-card__metric-value fg-coverage-card__metric-value--expiring">
                        {financial_overview.coverage_expiring || 0}
                      </div>
                    </div>
                    <div>
                      <div className="fg-coverage-card__metric-label">No Coverage (In-house)</div>
                      <div className="fg-coverage-card__metric-value fg-coverage-card__metric-value--nocov">
                        {financial_overview.no_coverage || 0}
                      </div>
                    </div>
                    <div>
                      <div className="fg-coverage-card__metric-label">Coverage Expired</div>
                      <div className="fg-coverage-card__metric-value fg-coverage-card__metric-value--expired">
                        {financial_overview.coverage_expired || 0}
                      </div>
                    </div>
                  </div>
                  <div className="fg-coverage-card__distribution">
                    <div className="fg-coverage-card__distribution-label">Service Distribution</div>
                    <div className="fg-coverage-card__distribution-stats">
                      {[
                        { icon: '🏥', label: 'Warranty', count: financial_overview.warranty_count || 0 },
                        { icon: '🔧', label: 'LAMC', count: financial_overview.lamc_count || 0 },
                        { icon: '⚙️', label: 'CAMC', count: financial_overview.camc_count || 0 },
                        { icon: '🏢', label: 'In-house', count: financial_overview.inhouse_count || 0 },
                      ].map((item, idx) => (
                        <Fragment key={item.label}>
                          {idx > 0 && <div className="fg-coverage-card__distribution-separator" aria-hidden="true" />}
                          <div className="fg-coverage-card__distribution-stat">
                            <div className="fg-coverage-card__distribution-stat-label">
                              {item.icon} {item.label}
                            </div>
                            <div className="fg-coverage-card__distribution-stat-value">{item.count}</div>
                          </div>
                        </Fragment>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {financial_overview.location_financial_summary && financial_overview.location_financial_summary.length > 0 && (
                <div className="sa-location-budget">
                  {(() => {
                    const locationRows = getSortedLocationBudgetRows(financial_overview.location_financial_summary);
                    const overBudgetCount = financial_overview.location_financial_summary.filter((loc) => loc.budget_overrun).length;
                    const locationCount = financial_overview.location_financial_summary.length;
                    const sortHeader = (column, label, numeric = false) => (
                      <th
                        className={`sortable${numeric ? ' num' : ''}`}
                        onClick={() => handleLocationBudgetSort(column)}
                      >
                        {label}{renderLocationBudgetSortIndicator(column)}
                      </th>
                    );
                    return (
                      <>
                  <div className="sa-location-budget__title">Location budget</div>
                  {overBudgetCount > 0 ? (
                    <div className="sa-location-budget__banner">
                      {overBudgetCount} of {locationCount} location{locationCount !== 1 ? 's' : ''} over budget
                    </div>
                  ) : (
                    <div className="sa-location-budget__meta">
                      {locationCount} location{locationCount !== 1 ? 's' : ''}
                    </div>
                  )}
                  <div className="sa-location-budget__legend">
                    <span>Utilization:</span>
                    <span><span className="sa-location-budget__swatch sa-location-budget__swatch--ok" />On track (≤75%)</span>
                    <span><span className="sa-location-budget__swatch sa-location-budget__swatch--watch" />Watch (76–90%)</span>
                    <span><span className="sa-location-budget__swatch sa-location-budget__swatch--over" />High (&gt;90%)</span>
                    <span><span className="sa-location-budget__swatch sa-location-budget__swatch--unbudgeted" />Unbudgeted</span>
                  </div>
                  <div className="sa-location-budget-table-wrap">
                    <table className="sa-location-budget-table">
                      <thead>
                        <tr>
                          {sortHeader('location', 'Location')}
                          {sortHeader('code', 'Code')}
                          {sortHeader('devices', 'Devices', true)}
                          {sortHeader('allocated', 'Allocated', true)}
                          {sortHeader('capex', 'Capex', true)}
                          {sortHeader('opex', 'Opex', true)}
                          {sortHeader('utilization', 'Utilization', true)}
                          {sortHeader('efficiency', 'Efficiency', true)}
                          {sortHeader('remaining', 'Remaining', true)}
                        </tr>
                      </thead>
                      <tbody>
                        {locationRows.map((loc) => {
                          const isOver = !!loc.budget_overrun;
                          return (
                            <tr key={loc.location_id || loc.location_name}>
                              <td className="sa-location-budget__name">{loc.location_name}</td>
                              <td>{loc.location_code}</td>
                              <td className="num">{loc.total_devices}</td>
                              <td className="num">{formatCurrencyDynamic(loc.allocated_budget || 0)}</td>
                              <td className="num">
                                {formatCurrencyDynamic(loc.capex_current_year ?? 0)}
                                <span className="sa-location-budget__sub">
                                  {formatCurrencyDynamic(loc.capex_all_time ?? 0)} all-time
                                </span>
                              </td>
                              <td className="num">
                                {formatCurrencyDynamic(loc.contracts_current_year ?? 0)}
                                <span className="sa-location-budget__sub">
                                  {formatCurrencyDynamic(loc.contracts_all_time ?? 0)} all-time
                                </span>
                              </td>
                              <td className="num">
                                <BudgetUtilizationDisplay loc={loc} compact />
                              </td>
                              <td className="num">{formatPercentage(loc.cost_efficiency_ratio)}</td>
                              <td className={`num ${isOver ? 'sa-location-budget__over' : 'sa-location-budget__ok'}`}>
                                {formatCurrencyDynamic(loc.budget_remaining || 0)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                      </>
                    );
                  })()}
                </div>
              )}
              <FinancialGovernanceHelpModal
                isOpen={showFinancialGovernanceHelp}
                onClose={() => setShowFinancialGovernanceHelp(false)}
                variant="superadmin"
              />
              <AssetCoverageByLocationModal
                isOpen={showCoverageByLocation}
                onClose={() => setShowCoverageByLocation(false)}
                rows={financial_overview.location_coverage_summary || []}
                onSelectLocation={(loc) => {
                  setShowCoverageByLocation(false);
                  setLocationSearchQuery(loc?.location_name || '');
                  scrollToSection('location-performance');
                }}
              />
              <HighCostApprovalsByLocationModal
                isOpen={showHighCostByLocation}
                onClose={() => setShowHighCostByLocation(false)}
                rows={financial_overview.location_high_cost_summary || []}
              />
            </>
          ) : (
            <EmptyState section="Financial Overview" message="No financial data available." icon="💰" />
          )}
        </Section>

        {/* ========================================
            SECTION 4: USER MANAGEMENT
            ======================================== */}
        <Section id="user-management" title="👥 User Management & Access Control">
          {user_management && Object.keys(user_management).length > 0 ? (
            (() => {
              const locationGroups = buildLocationRoleGroups(user_management.location_role_summary || []);
              const canOpenUsers = typeof onNavigateToUsers === 'function';
              const totals = {
                active: user_management.total_active || 0,
                inactive: user_management.inactive_users || 0,
                pending: user_management.pending_verifications || 0,
              };
              const grandTotal = totals.active + totals.inactive + totals.pending;
              const toggleLocation = (key) => {
                setExpandedUserLocationKeys((prev) => {
                  const next = new Set(prev);
                  if (next.has(key)) next.delete(key);
                  else next.add(key);
                  return next;
                });
              };

              const accountReadiness = user_management.account_readiness || {};
              const readinessBreakdown = accountReadiness.non_compliant_breakdown || {};
              const readinessGapItems = [
                { key: 'pending_verification', label: 'Unverified email', count: readinessBreakdown.pending_verification },
                { key: 'password_setup_pending', label: 'Password setup pending', count: readinessBreakdown.password_setup_pending },
                { key: 'password_reset_required_active', label: 'Password reset required (active)', count: readinessBreakdown.password_reset_required_active },
                { key: 'no_location_assignment', label: 'No location assigned', count: readinessBreakdown.no_location_assignment },
                { key: 'missing_department', label: 'Missing department', count: readinessBreakdown.missing_department },
              ].filter((item) => item.count > 0);
              const readinessGapTotal = readinessBreakdown.total
                || readinessGapItems.reduce((sum, item) => sum + item.count, 0);
              const attentionCount = Math.max(
                (accountReadiness.total || 0) - (accountReadiness.ready || 0),
                readinessGapTotal,
              );

              const roster = locationGroups.length === 0 ? (
                <EmptyState section="User Management" message="No location roster data available." icon="👥" />
              ) : (
                <>
                  <div className="dashboard-subsection-label">By location</div>
                  <div className="user-mgmt-role-table-wrap">
                    <table className="user-mgmt-role-table sa-user-role-table">
                      <thead>
                        <tr>
                          <th className="user-mgmt-role-table__role-col">Location</th>
                          <th className="user-mgmt-role-table__num-col user-mgmt-role-table__num-col--active">Active</th>
                          <th className="user-mgmt-role-table__num-col user-mgmt-role-table__num-col--inactive">Inactive</th>
                          <th className="user-mgmt-role-table__num-col user-mgmt-role-table__num-col--pending">Pending</th>
                          <th className="user-mgmt-role-table__num-col">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {locationGroups.map((loc) => {
                          const locKey = String(loc.location_id ?? 'unassigned');
                          const expanded = expandedUserLocationKeys.has(locKey);
                          const locTotal = loc.active + loc.inactive + loc.pending;
                          return (
                            <Fragment key={locKey}>
                              <tr
                                className={`sa-user-role-table__rollup${expanded ? ' sa-user-role-table__rollup--open' : ''}`}
                                onClick={() => toggleLocation(locKey)}
                              >
                                <td className="user-mgmt-role-table__role">
                                  <span className="sa-user-role-table__loc">
                                    <span className="sa-user-role-table__chevron" aria-hidden="true">
                                      {expanded ? '▾' : '▸'}
                                    </span>
                                    <span>
                                      {loc.location_name}
                                      {loc.location_code && loc.location_code !== 'N/A' && (
                                        <span className="sa-user-location-row__code">{loc.location_code}</span>
                                      )}
                                    </span>
                                    {canOpenUsers && loc.location_id && (
                                      <button
                                        type="button"
                                        className="sa-user-role-table__users-link"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          onNavigateToUsers(null, [loc.location_id]);
                                        }}
                                      >
                                        Users →
                                      </button>
                                    )}
                                  </span>
                                </td>
                                <td className="user-mgmt-role-table__num">{loc.active}</td>
                                <td className="user-mgmt-role-table__num">{loc.inactive}</td>
                                <td className="user-mgmt-role-table__num">{loc.pending}</td>
                                <td className="user-mgmt-role-table__num user-mgmt-role-table__num--total">{locTotal}</td>
                              </tr>
                              {expanded && loc.roles.map((roleItem, idx) => (
                                <tr key={`${locKey}-role-${roleItem.role || 'none'}-${idx}`} className="sa-user-role-table__role-row">
                                  <td className="user-mgmt-role-table__role">
                                    <span className="sa-user-role-table__role-name">
                                      {roleItem.role ? formatRoleName(roleItem.role) : '—'}
                                    </span>
                                  </td>
                                  <td className="user-mgmt-role-table__num">{roleItem.active}</td>
                                  <td className="user-mgmt-role-table__num">{roleItem.inactive}</td>
                                  <td className="user-mgmt-role-table__num">{roleItem.pending}</td>
                                  <td className="user-mgmt-role-table__num user-mgmt-role-table__num--total">
                                    {roleItem.active + roleItem.inactive + roleItem.pending}
                                  </td>
                                </tr>
                              ))}
                            </Fragment>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td className="user-mgmt-role-table__role user-mgmt-role-table__role--total">Total</td>
                          <td className="user-mgmt-role-table__num user-mgmt-role-table__num--total">{totals.active}</td>
                          <td className="user-mgmt-role-table__num user-mgmt-role-table__num--total">{totals.inactive}</td>
                          <td className="user-mgmt-role-table__num user-mgmt-role-table__num--total">{totals.pending}</td>
                          <td className="user-mgmt-role-table__num user-mgmt-role-table__num--total">{grandTotal}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                  <p className="sa-user-location-hint">
                    One row per location. Select a row to see roles.
                    Total is unique people across the organization (you are not included).
                  </p>
                </>
              );

              return (
                <>
                  <div className="dashboard-subsection-label">Account readiness</div>
                  <div className="dashboard-stats-summary" style={{ marginBottom: '0.65rem' }}>
                    <ComplianceRateStatCard icon="👤" rate={accountReadiness.rate} label="Ready" />
                    <StatsCard
                      icon="✅"
                      number={accountReadiness.ready || 0}
                      label="Access-ready"
                      className="active"
                      subtext={`${accountReadiness.total || 0} in scope`}
                    />
                    <StatsCard
                      icon="⚠️"
                      number={attentionCount}
                      label="Need Attention"
                      className={`open${attentionCount > 0 ? ' alert-elevated' : ''}`}
                      title="Users missing verification, password, location, or department"
                      onClick={canOpenUsers ? () => onNavigateToUsers() : undefined}
                    />
                  </div>
                  {readinessGapItems.length > 0 && (
                    <div className="sa-readiness-gaps">
                      <span className="sa-readiness-gaps__label">Gaps ({readinessGapTotal})</span>
                      {readinessGapItems.map((item) => (
                        <span key={item.key} className="sa-readiness-chip">
                          {item.count} {item.label}
                        </span>
                      ))}
                      {canOpenUsers ? (
                        <button
                          type="button"
                          className="dashboard-inline-link"
                          onClick={() => onNavigateToUsers()}
                        >
                          View users →
                        </button>
                      ) : (
                        <a href="#user-management" className="dashboard-inline-link">View users →</a>
                      )}
                    </div>
                  )}
                  {roster}
                </>
              );
            })()
          ) : (
            <EmptyState section="User Management" message="No user data available." icon="👥" />
          )}
        </Section>

        {/* ========================================
            SECTION 5: AUDIT & COMPLIANCE
            ======================================== */}
        <Section
          id="audit-events"
          title={(
            <span
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
                gap: '1rem',
              }}
            >
              <span>🔍 Device Audit & Compliance Tracking</span>
              <button
                type="button"
                className="user-mgmt-help-btn"
                onClick={() => setShowDeviceAuditHelp(true)}
                aria-label="Device audit and compliance help"
                title="What do these metrics mean?"
                style={{ flexShrink: 0 }}
              >
                ?
              </button>
            </span>
          )}
        >
          {hasAuditComplianceData ? (
            <AuditComplianceSection
              variant="superadmin"
              audit_events={audit_events}
              compliance_status={compliance_status}
              auditDepartmentFilter={auditLocationFilter}
              setAuditDepartmentFilter={setAuditLocationFilter}
              auditQuickFilter={auditQuickFilter}
              setAuditQuickFilter={setAuditQuickFilter}
              selectedAuditEvent={selectedAuditEvent}
              setSelectedAuditEvent={setSelectedAuditEvent}
              isAuditDrawerOpen={isAuditDrawerOpen}
              setIsAuditDrawerOpen={setIsAuditDrawerOpen}
              deptSearchQuery={deptSearchQuery}
              setDeptSearchQuery={setDeptSearchQuery}
              nonCompliantSearchQuery={nonCompliantSearchQuery}
              setNonCompliantSearchQuery={setNonCompliantSearchQuery}
              showDeviceAuditHelp={showDeviceAuditHelp}
              setShowDeviceAuditHelp={setShowDeviceAuditHelp}
              onNavigateToDevices={onNavigateToDevices}
            />
          ) : (
            <EmptyState
              section="Audit & Compliance"
              message="No audit or compliance data available yet."
              icon="📋"
            />
          )}
        </Section>

        {/* ========================================
            SECTION 6: LOCATION PERFORMANCE
            ======================================== */}
        <Section id="location-performance" title="📊 Location Performance Overview">
          {location_performance.locations && location_performance.locations.length > 0 ? (
            <>
              <input
                type="text"
                className="cs-search-input"
                placeholder="Search locations..."
                value={locationSearchQuery}
                onChange={(e) => setLocationSearchQuery(e.target.value)}
                aria-label="Search locations"
              />
              <div className="cs-rate-legend" aria-label="Compliance color ranges">
                <span className="cs-rate-legend__item">
                  <span className="cs-rate-badge cs-rate-badge--ok">●</span> ≥80%
                </span>
                <span className="cs-rate-legend__item">
                  <span className="cs-rate-badge cs-rate-badge--warn">●</span> 60–79%
                </span>
                <span className="cs-rate-legend__item">
                  <span className="cs-rate-badge cs-rate-badge--bad">●</span> &lt;60%
                </span>
                <span className="cs-rate-legend__item">
                  <span className="cs-rate-badge cs-rate-badge--na">--</span> N/A
                </span>
              </div>
              <div className="sa-location-budget-table-wrap">
                <table className="sa-location-budget-table">
                  <thead>
                    <tr>
                      <th>Location</th>
                      <th className="num">Devices</th>
                      <th className="num">Users</th>
                      <th className="num">Admins</th>
                      <th className="num" title="Devices with overdue PPM, calibration, or regulatory maintenance (same as At-Risk Locations)">Overdue</th>
                      <th className="num" title="Open complaints">Complaints</th>
                      <th className="num" title="PPM on-time rate for active devices">PPM</th>
                      <th className="num" title="Calibration on-time rate for devices that require it">Cal.</th>
                      <th className="num" title="Regulatory on-time rate for devices that require it">Reg.</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {location_performance.locations
                      .filter((loc) => loc.location_name.toLowerCase().includes(locationSearchQuery.toLowerCase()))
                      .map((loc) => {
                        const overdue = loc.overdue_maintenance_count || 0;
                        const active = isLocationActive(loc);
                        return (
                        <tr key={loc.location_id || loc.location_name}>
                          <td className="sa-location-budget__name">
                            {loc.location_name}
                            {loc.location_code && loc.location_code !== 'N/A' && (
                              <span className="sa-location-budget__sub">{loc.location_code}</span>
                            )}
                          </td>
                          <td className="num">{loc.total_devices}</td>
                          <td className="num">{loc.total_users}</td>
                          <td className="num">{loc.admin_count || 0}</td>
                          <td className="num">
                            <span className={overdue > 0 ? 'sa-location-budget__over' : undefined}>
                              {overdue}
                            </span>
                          </td>
                          <td className="num">
                            <span className={loc.active_complaints > 0 ? 'sa-location-budget__over' : undefined}>
                              {loc.active_complaints}
                            </span>
                            {loc.critical_complaints > 0 && (
                              <span className="sa-location-budget__sub sa-location-perf__critical">
                                {loc.critical_complaints} critical
                              </span>
                            )}
                          </td>
                          <td className="num">
                            <LocationPerformanceRate rate={loc.ppm_compliance_rate ?? loc.device_compliance_rate} />
                          </td>
                          <td className="num">
                            <LocationPerformanceRate rate={loc.calibration_compliance_rate} />
                          </td>
                          <td className="num">
                            <LocationPerformanceRate rate={loc.regulatory_compliance_rate} />
                          </td>
                          <td className={active ? undefined : 'sa-location-perf__inactive'}>
                            {active ? 'Active' : 'Inactive'}
                          </td>
                        </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
              {location_performance.locations.filter((loc) => loc.location_name.toLowerCase().includes(locationSearchQuery.toLowerCase())).length === 0 && locationSearchQuery && (
                <div className="cs-empty-search">No locations match your search</div>
              )}
            </>
          ) : (
            <EmptyState section="Location Performance" message="No location data available." icon="📍" />
          )}
        </Section>

        {/* ========================================
            COMPLAINT VIEW MODAL
            Displays complaint details when a row is clicked
            ======================================== */}
        {selectedComplaintForModal && (
          <ComplaintView
            complaintId={selectedComplaintForModal.id}
            token={token}
            onClose={() => setSelectedComplaintForModal(null)}
            onUpdate={() => {
              // For super admin dashboard, refresh might not be necessary as they see aggregated data
              // But we can add it for consistency if needed
            }}
            isFullPage={false}
            user={user}
          />
        )}

      </DashboardLayout>
    );
  };

  return (
    <BaseDashboard 
      token={token} 
      user={user}
    >
      {renderDashboard}
    </BaseDashboard>
  );
};

export default SuperAdminDashboard;
